// Single live-call instance per browser (across tabs).
//
// Why: prod ClickHouse showed every user runs ~2 tabs IN A CALL at once
// (Sergey_QA: 8 minutes with 2 concurrent diag sessions). Two tabs in a call =
// two DFN denoiser workers on one machine → CPU contention → DFN inference
// blows the 9ms/frame budget → degrade/flap, plus double-mic and echo. So a
// "powerful idle machine" still flapped because it was busy with OUR own second
// tab. Fix: only ONE tab owns the live voice runtime at a time.
//
// Mechanism:
//   • Ownership = an EXCLUSIVE Web Lock ('rx2go-voice-call'). The browser
//     auto-releases it when the owning tab closes / crashes / navigates — no
//     stale-lock problem (the reason we don't hand-roll localStorage heartbeats).
//   • Takeover signalling = a BroadcastChannel. A second tab that wants the call
//     ("use here") broadcasts 'takeover'; the current owner hears it, leaves its
//     call (→ releases the lock), and the new tab then acquires it.
//
// Graceful fallback: if Web Locks / BroadcastChannel are unavailable (very old
// browsers), every call resolves to "owned" so behaviour is exactly as before
// (no guard) — we never block a user out of a call because the primitive is
// missing.

const LOCK_NAME = 'rx2go-voice-call'
const CHANNEL_NAME = 'rx2go-voice-instance'

let held = false
let releaseHeld: (() => void) | null = null
let bc: BroadcastChannel | null = null
let takeoverCb: (() => void) | null = null

export function isCallGuardSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.locks &&
    typeof BroadcastChannel !== 'undefined'
  )
}

/** Are WE (this tab) the current owner of the live call? */
export function ownsCall(): boolean {
  return held
}

function ensureChannel(): BroadcastChannel | null {
  if (!isCallGuardSupported()) return null
  if (!bc) {
    bc = new BroadcastChannel(CHANNEL_NAME)
    bc.onmessage = (e: MessageEvent) => {
      // Another tab is taking the call over. If we hold it, leave (the store's
      // callback runs disconnect → releaseCallOwnership). If we don't hold it,
      // ignore.
      if (e?.data?.type === 'takeover' && held) {
        try { takeoverCb?.() } catch { /* noop */ }
      }
    }
  }
  return bc
}

/**
 * Register the callback fired when ANOTHER tab requests takeover and we are the
 * current owner. The owner should leave its call here (which releases the lock).
 * Single listener — livekitStore wires `disconnect`.
 */
export function onCallTakeoverRequested(cb: (() => void) | null): void {
  takeoverCb = cb
  ensureChannel()
}

/**
 * Acquire call ownership if it's free. Resolves true if we now hold it (or
 * already did), false if another tab currently owns the call. Never throws.
 */
export function tryAcquireCallOwnership(): Promise<boolean> {
  if (!isCallGuardSupported()) return Promise.resolve(true)
  if (held) return Promise.resolve(true)
  ensureChannel()
  return new Promise<boolean>((resolve) => {
    let settled = false
    navigator.locks
      .request(LOCK_NAME, { mode: 'exclusive', ifAvailable: true }, (lock) => {
        if (!lock) {
          // Held by another tab.
          if (!settled) { settled = true; resolve(false) }
          return undefined
        }
        held = true
        // Hold the lock by keeping this callback's promise pending until
        // releaseCallOwnership() resolves it.
        return new Promise<void>((release) => {
          releaseHeld = () => {
            held = false
            releaseHeld = null
            release()
          }
          if (!settled) { settled = true; resolve(true) }
        })
      })
      .catch(() => { if (!settled) { settled = true; resolve(false) } })
  })
}

/** Release call ownership (call on leaving the call). Safe to call when unheld. */
export function releaseCallOwnership(): void {
  try { releaseHeld?.() } catch { /* noop */ }
}

/** Forcibly steal the lock — last resort if the old owner is unresponsive. */
function stealCallOwnership(): Promise<boolean> {
  if (!isCallGuardSupported()) return Promise.resolve(true)
  return new Promise<boolean>((resolve) => {
    let settled = false
    navigator.locks
      .request(LOCK_NAME, { mode: 'exclusive', steal: true }, (lock) => {
        if (!lock) { if (!settled) { settled = true; resolve(false) }; return undefined }
        held = true
        return new Promise<void>((release) => {
          releaseHeld = () => { held = false; releaseHeld = null; release() }
          if (!settled) { settled = true; resolve(true) }
        })
      })
      .catch(() => { if (!settled) { settled = true; resolve(false) } })
  })
}

/**
 * "Use here": ask the current owner tab to leave, then take the call. Broadcasts
 * a takeover request, polls for the freed lock (the owner releases on hearing
 * the broadcast), and steals as a last resort if the owner never responds.
 * Resolves true once we hold the lock.
 */
export async function requestCallTakeover(timeoutMs = 3000): Promise<boolean> {
  if (!isCallGuardSupported()) return true
  if (held) return true
  ensureChannel()?.postMessage({ type: 'takeover' })
  const deadline = Date.now() + timeoutMs
  // Poll: the owner disconnects on the broadcast and releases the lock; the next
  // ifAvailable acquire then succeeds.
  while (Date.now() < deadline) {
    if (await tryAcquireCallOwnership()) return true
    await new Promise((r) => setTimeout(r, 150))
  }
  // Owner never released (crashed mid-handler / unresponsive) → steal it.
  return stealCallOwnership()
}
